/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import { forwardRef, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/field/Button';

import useAlert from 'hooks/useAlert';
import useIsOverflow from 'hooks/useIsOverflow';
import useLocale from 'hooks/useLocale';
import useMergedRef from 'hooks/useMergedRef';

import { faCopy } from '@fortawesome/pro-regular-svg-icons';

import Popper from './Popper';
import Tooltip from './Tooltip';
import * as S from './styled/Typography.styled';

const config = {
	display: {
		fontSize: 'var(--display-font-size)',
		lineHeight: 'var(--display-line-height)',
		fontWeight: '700'
	},
	h1: {
		fontSize: 'var(--heading1-font-size)',
		lineHeight: 'var(--heading1-line-height)',
		fontWeight: '700'
	},
	h2: {
		fontSize: 'var(--heading2-font-size)',
		lineHeight: 'var(--heading2-line-height)',
		fontWeight: '700'
	},
	h3: {
		fontSize: 'var(--heading3-font-size)',
		lineHeight: 'var(--heading3-line-height)',
		fontWeight: '700'
	},
	h4: {
		fontSize: 'var(--heading4-font-size)',
		lineHeight: 'var(--heading4-line-height)',
		fontWeight: '700'
	},
	p1: {
		fontSize: 'var(--paragraph1-font-size)',
		lineHeight: 'var(--paragraph1-line-height)',
		fontWeight: '400'
	},
	p2: {
		fontSize: 'var(--paragraph2-font-size)',
		lineHeight: 'var(--paragraph2-line-height)',
		fontWeight: '400'
	},
	p3: {
		fontSize: 'var(--paragraph3-font-size)',
		lineHeight: 'var(--paragraph3-line-height)',
		fontWeight: '400'
	},
	caption: {
		fontSize: 'var(--caption-font-size)',
		lineHeight: 'var(--caption-line-height)',
		fontWeight: '400'
	},
	l1: {
		fontSize: 'var(--link1-font-size)',
		lineHeight: 'var(--link1-line-height)',
		fontWeight: '500',
		className: 'link'
	},
	l2: {
		fontSize: 'var(--link2-font-size)',
		lineHeight: 'var(--link2-line-height)',
		fontWeight: '500',
		className: 'link'
	},
	l3: {
		fontSize: 'var(--link3-font-size)',
		lineHeight: 'var(--link3-line-height)',
		fontWeight: '500',
		className: 'link'
	},
	l4: {
		fontSize: 'var(--link4-font-size)',
		lineHeight: 'var(--link4-line-height)',
		fontWeight: '500',
		className: 'link'
	}
};

/**
 * @author Burak Unsal
 * @description The Typography component makes it easy to apply a default set of sizes and styling text elements in our application.
 * @param {Object} props - props of the typography component
 * @param {'display' | 'h1' | 'h2' | 'h3' | 'h4' | 'p1' | 'p2' | 'p3' | 'caption' | 'l1' | 'l2' | 'l3' | 'l4'} [props.variant = 'p3'] - Applies the ready to use styles.
 * @param {'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'li' | 'label'} [props.component = 'span'] - The component used for the root node. Either a string to use a HTML element or a component.
 * @param {'center' | 'left' | 'right' | 'justify'} props.align - The text alignment.
 * @param {boolean} [props.noWrap = false] - If true, the text won't wrap, but instead the text will truncate with a text overflow ellipsis.
 * @param {boolean} [props.gutterBottom = false] - If true, the text will have a bottom margin.
 * @param {boolean} [props.fullWidth = false] - If true, the component will have a full width.
 * @param {number} [props.maxlines] - If set some number value, the component will display only the maximum number of given this value lines.
 * @param {string} [props.className] - Adds classname to the component. Override or extend the styles applied to the component.
 * @param {Object} [props.css] - Adds css to the styled component. Override or extend the styles applied to the component.
 * @param {string} [props.color] - Color of the text.
 * @param {string} [props.fontSize] - Font size of the text.
 * @param {'400' | '500' | '700'} [props.fontWeight] - Font weight of the text.
 * @param {boolean} [props.withCurrency = false] - If true, the component will display the currency symbol.
 * @param {string} [props.currency = 'auto'] - The currency symbol to be displayed. If the value is 'auto', the component will display the currency symbol according to the locale.
 * @param {'number' | 'string'} [props.format] - The format of the text. If the value is 'number', the component will display the number with the locale format.
 * @param {*} props.children - The content of the component. The component use the i18n translation for the string values.
 * @returns {object} - Text object with the given props
 * @example
 * <Typography variant="h1" component="h1" noWrap gutterBottom>
 *	test // The component use the i18n translation for the string values.
 * </Typography>
 *
 */
const Typography = (props, ref) => {
	const {
		variant = 'p3',
		component = 'span',
		noWrap = false,
		gutterBottom = false,
		css: cssProp,
		className,
		children,
		fullWidth = false,
		maxlines,
		withCurrency = false,
		currency: currencyProp = 'auto',
		autoTranslation = true,
		fontSize: fontSizeProp,
		fontWeight: fontWeightProp,
		showTooltip = true,
		showCopyButton = false,
		format,
		...moreProps
	} = props;

	const { t } = useTranslation();
	const { Currency, LangCountryCode } = useLocale();
	const { showInfo } = useAlert();
	const [typographyRef, setTypographyRef] = useState(null);
	const refs = useMergedRef(ref, setTypographyRef);
	const isOverflow = useIsOverflow(typographyRef);

	const {
		fontSize,
		lineHeight,
		fontWeight,
		className: variantClassName
	} = useMemo(
		() =>
			config[variant] ?? {
				fontSize: 'var(--paragraph3-font-size)',
				lineHeight: 'var(--paragraph3-line-height)',
				fontWeight: '400'
			},
		[variant]
	);

	const formatNumber = useCallback(
		(value, alwaysFormat) =>
			format === 'number' || alwaysFormat
				? new Intl.NumberFormat(LangCountryCode.replace('_', '-'), {
						useGrouping: true,
						numberingSystem: 'latn'
					}).format(value)
				: value,
		[LangCountryCode]
	);

	const content =
		typeof children === 'string' || typeof children === 'number'
			? withCurrency
				? `${formatNumber(children?.toFixed(2), true)} ${t(currencyProp === 'auto' ? Currency : currencyProp)}`
				: autoTranslation
					? t(formatNumber(children))
					: formatNumber(children)
			: formatNumber(children);

	const handleCopy = (e) => {
		e.stopPropagation();
		e.preventDefault();

		navigator.clipboard.writeText(content);
		showInfo({
			header: 'copiedToClipboard'
		});
	};

	const languageKey =
		typeof children === 'string' && !withCurrency && autoTranslation && content !== children ? children : undefined;

	return (
		<>
			<S.Typography
				maxlines={maxlines}
				innerRef={refs}
				tag={component}
				css={css`
					font-size: ${fontSizeProp ?? fontSize};
					/* ${lineHeight ? `line-height: ${lineHeight};` : ''} */
					font-weight: ${fontWeightProp ?? fontWeight};
					${showCopyButton ? ':hover{color: var(--blue);}' : ''}
					${cssProp}
				`}
				className={`typography ${className ?? ''} ${variant} ${noWrap ? 'noWrap ' : ''}${
					gutterBottom ? 'gutterBottom ' : ''
				}${fullWidth ? 'fullWidth ' : ''}${maxlines ? 'maxlines ' : ''}${variantClassName ?? ''}`}
				langkey={languageKey}
				{...moreProps}>
				{content}
			</S.Typography>
			{showCopyButton && (
				<Popper
					refElement={typographyRef}
					hoverMode
					timeout={1000}
					placement="left"
					offsetY={4}
					showOnMobile={false}>
					{() => <Button variant="action" size="x-small" icon={faCopy} onClick={handleCopy} />}
				</Popper>
			)}
			{showTooltip && isOverflow && (
				<Tooltip
					componentRef={typographyRef}
					title={content}
					placement="bottom"
					containerCss={S.TooltipContainerCss}
					popperContainerCss={S.TooltipPopperCss}
					offsetY={-4}
					showOnMobile={false}
				/>
			)}
		</>
	);
};

/**
 * @author Burak Unsal
 * @description The Typography component makes it easy to apply a default set of sizes and styling text elements in our application.
 * @param {Function} Typography
 * @param {Object} props - props of the typography component
 * @param {'display' | 'h1' | 'h2' | 'h3' | 'h4' | 'p1' | 'p2' | 'p3' | 'caption' | 'l1' | 'l2' | 'l3' | 'l4'} [props.variant = 'p3'] - Applies the ready to use styles.
 * @param {'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'li' | 'label'} [props.component = 'span'] - The component used for the root node. Either a string to use a HTML element or a component.
 * @param {boolean} [props.noWrap = false] - If true, the text won't wrap, but instead the text will truncate with a text overflow ellipsis.
 * @param {boolean} [props.gutterBottom = false] - If true, the text will have a bottom margin.
 * @param {boolean} [props.fullWidth = false] - If true, the component will have a full width.
 * @param {number} [props.maxlines] - If set some number value, the component will display only the maximum number of given this value lines.
 * @param {string} [props.className] - Adds classname to the component. Override or extend the styles applied to the component.
 * @param {Object} [props.css] - Adds css to the styled component. Override or extend the styles applied to the component.
 * @param {string} [props.color] - Color of the text.
 * @param {string} [props.fontSize] - Font size of the text.
 * @param {'400' | '500' | '700'} [props.fontWeight] - Font weight of the text.
 * @param {boolean} [props.withCurrency = false] - If true, the component will display the currency symbol.
 * @param {string} [props.currency = 'auto'] - The currency symbol to be displayed. If the value is 'auto', the component will display the currency symbol according to the locale.
 * @param {'number' | 'string'} [props.format] - The format of the text. If the value is 'number', the component will display the number with the locale format.
 * @param {*} props.children - The content of the component. The component use the i18n translation for the string values.
 * @returns {object} - Text object with the given props
 * @example
 * <Typography variant="h1" component="h1" noWrap gutterBottom>
 *	test // The component use the i18n translation for the string values.
 * </Typography>
 *
 */

export default forwardRef(Typography);
